import React from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate, useLocation } from 'react-router-dom';
import logo from './logo.svg';
import './App.css';
import { useEffect } from 'react';
import { loadStripeJs } from './utils';
import { supabase } from './supabaseClient';
import { FaUserSecret, FaRobot, FaCreditCard, FaDesktop, FaChrome, FaServer } from 'react-icons/fa';
import { format, addDays, parseISO } from 'date-fns';

function Navbar() {
  const [user, setUser] = React.useState(null);
  React.useEffect(() => {
    let ignore = false;
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!ignore) setUser(user);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null);
    });
    return () => {
      ignore = true;
      listener.subscription.unsubscribe();
    };
  }, []);

  return (
    <nav className="bg-primary-dark text-white px-6 py-4 flex items-center justify-between shadow-xl border-b border-primary-neon/30 z-10">
      <div className="flex items-center gap-3">
        <img src={process.env.PUBLIC_URL + '/icon48.png'} alt="FuckHonorLock Logo" className="h-10 w-10 drop-shadow-neon" />
        <span className="text-2xl font-extrabold tracking-wide text-primary-neon drop-shadow-neon">FuckHonorLock</span>
      </div>
      <ul className="flex gap-8 text-base font-semibold">
        <li><a href="/" className="hover:text-primary-neon transition">Home</a></li>
        <li><a href="/features" className="hover:text-primary-neon transition">Features</a></li>
        <li><a href="/settings/billing" className="hover:text-primary-neon transition">Billing</a></li>
        <li><a href="/auth" className={user ? "hover:text-primary-neon transition" : "hover:text-primary-edge transition"}>{user ? "Profile" : "Login/Signup"}</a></li>
      </ul>
    </nav>
  );
}

function PlanSelection() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-main-gradient bg-radial-overlay text-white flex flex-col">
      <Navbar />
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-8 md:py-0 mt-16">
        <section className="max-w-2xl w-full text-center mb-12 mt-12 md:mt-0 flex flex-col items-center justify-center">
          <h1 className="text-5xl md:text-6xl font-extrabold mb-4 text-primary-neon drop-shadow-neon">Bypass Honorlock. Take Control.</h1>
          <p className="text-lg md:text-2xl mb-8 text-text-light font-medium drop-shadow">The ultimate toolkit for acing your online exams. Invisible desktop app + AI-powered Chrome extension. Outsmart the system, get answers fast, and never stress a test again.</p>
          <button
            className="bg-gradient-to-r from-primary-neon to-primary-edge text-primary-dark font-bold px-10 py-4 rounded-full shadow-xl hover:from-primary-edge hover:to-primary-neon hover:text-white transition text-xl ring-2 ring-primary-neon hover:ring-primary-edge focus:outline-none focus:ring-4 focus:ring-primary-neon/60"
            onClick={() => navigate('/purchase/basic')}
          >
            Get Started
          </button>
        </section>
        <section className="grid md:grid-cols-2 gap-10 w-full max-w-4xl mb-20">
          <div className="bg-primary-surface rounded-2xl p-10 shadow-neon border-2 border-primary-neon hover:scale-105 hover:shadow-neon transition-transform duration-200 relative overflow-hidden group">
            <FaUserSecret className="absolute top-4 right-4 text-primary-neon text-4xl opacity-30 group-hover:opacity-60 transition" />
            <h2 className="text-2xl font-extrabold mb-2 text-primary-neon drop-shadow-neon">Stealth Desktop App</h2>
            <p className="mb-4 text-primary-neon/90">Runs at the highest window level, invisible to screenshare and proctoring apps. Use your resources freely during Honorlock exams.</p>
            <span className="inline-block bg-primary-dark text-primary-neon px-3 py-1 rounded-full text-xs font-semibold border border-primary-neon/50">Bypass Detection</span>
          </div>
          <div className="bg-primary-surface rounded-2xl p-10 shadow-edge border-2 border-primary-edge hover:scale-105 hover:shadow-edge transition-transform duration-200 relative overflow-hidden group">
            <FaRobot className="absolute top-4 right-4 text-primary-edge text-4xl opacity-30 group-hover:opacity-60 transition" />
            <h2 className="text-2xl font-extrabold mb-2 text-primary-edge drop-shadow-edge">AI Chrome Extension</h2>
            <p className="mb-4 text-primary-edge/90">Ask questions, take screenshots, and get instant AI answers. Seamlessly integrates with your workflow for fast, accurate help.</p>
            <span className="inline-block bg-primary-dark text-primary-edge px-3 py-1 rounded-full text-xs font-semibold border border-primary-edge/50">AI Powered</span>
          </div>
        </section>
        <section className="w-full max-w-3xl flex flex-col items-center">
          <h3 className="text-2xl font-extrabold mb-6 text-primary-neon drop-shadow-neon text-center">Choose Your Plan</h3>
          <ul className="flex flex-col md:flex-row gap-8 justify-center">
            <li>
              <div className="bg-primary-surface p-8 rounded-xl shadow-neon border-2 border-primary-neon flex flex-col items-center hover:scale-105 hover:shadow-neon transition-transform duration-200 w-72 text-center">
                <span className="text-xl font-bold mb-2 text-primary-neon">Basic Plan</span>
                <span className="mb-1 text-2xl font-extrabold text-primary-neon">$5<span className="text-lg font-semibold">/mo</span></span>
                <span className="mb-4 text-primary-neon/80">300 credits/month</span>
                <span className="mb-6 text-primary-neon/80">Access to desktop app</span>
                <button className="w-full bg-gradient-to-r from-primary-neon to-primary-edge text-primary-dark font-bold px-8 py-3 rounded mb-1 hover:from-primary-edge hover:to-primary-neon hover:text-white transition ring-2 ring-primary-neon hover:ring-primary-edge text-lg" onClick={() => navigate('/purchase/basic')}>Buy Basic</button>
              </div>
            </li>
            <li>
              <div className="bg-primary-surface p-8 rounded-xl shadow-neon border-2 border-primary-neon flex flex-col items-center hover:scale-105 hover:shadow-neon transition-transform duration-200 w-72 text-center">
                <span className="text-xl font-bold mb-2 text-primary-neon">Pro Plan</span>
                <span className="mb-1 text-2xl font-extrabold text-primary-neon">$15<span className="text-lg font-semibold">/mo</span></span>
                <span className="mb-4 text-primary-neon/80">500 credits/month</span>
                <span className="mb-6 text-primary-neon/80">Desktop app + Chrome extension</span>
                <button className="w-full bg-gradient-to-r from-primary-neon to-primary-edge text-primary-dark font-bold px-8 py-3 rounded mb-1 hover:from-primary-edge hover:to-primary-neon hover:text-white transition ring-2 ring-primary-neon hover:ring-primary-edge text-lg" onClick={() => navigate('/purchase/pro')}>Buy Pro</button>
              </div>
            </li>
            <li>
              <div className="bg-primary-surface p-8 rounded-xl shadow-edge border-2 border-primary-edge flex flex-col items-center hover:scale-105 hover:shadow-edge transition-transform duration-200 w-72 text-center">
                <span className="text-xl font-bold mb-2 text-primary-edge">Premium Plan</span>
                <span className="mb-1 text-2xl font-extrabold text-primary-edge">$25<span className="text-lg font-semibold">/mo</span></span>
                <span className="mb-4 text-primary-edge/80">1000 credits/month</span>
                <span className="mb-6 text-primary-edge/80">All features + priority support</span>
                <button className="w-full bg-gradient-to-r from-primary-edge to-primary-neon text-white font-bold px-8 py-3 rounded mb-1 hover:from-primary-neon hover:to-primary-edge hover:text-primary-dark transition ring-2 ring-primary-edge hover:ring-primary-neon text-lg" onClick={() => navigate('/purchase/premium')}>Buy Premium</button>
              </div>
            </li>
          </ul>
        </section>
      </main>
      <footer className="bg-primary-dark text-primary-neon text-center py-4 text-xs opacity-90 border-t-2 border-primary-neon/30 shadow-inner mt-12">
        &copy; {new Date().getFullYear()} FuckHonorLock. Not affiliated with Honorlock. For educational use only.
      </footer>
    </div>
  );
}

function FeaturesPage() {
  const features = [
    {
      section: 'Desktop App',
      icon: <FaDesktop className="text-4xl text-primary-neon mb-2" />,
      items: [
        'Frameless, draggable, resizable overlay with adjustable transparency and color',
        'Global hotkeys for instant screenshot and AI analysis (Alt+L to trigger, Alt+Up/Down to scroll)',
        'Math-aware AI: Automatically formats math in LaTeX for clarity',
        'Settings dialog: Change font size, background color, opacity, scroll speed, and privacy mode',
        'Backend API: Node.js server using Google Gemini for multimodal (image+text) or text-only queries',
        'Privacy: Option to exclude overlay from screen capture',
      ],
    },
    {
      section: 'Chrome Extension',
      icon: <FaChrome className="text-4xl text-primary-edge mb-2" />,
      items: [
        'Multi-session chat interface with Gemini AI',
        'Automatic chat session creation on typing',
        'Delete individual chats or clear all',
        'Screenshot selection overlay with cancel option (does not appear in screenshots)',
        'File upload (images, PDFs, text)',
        'Persistent chat history (localStorage)',
        'Live credits polling and user authentication',
        'Seamless integration with browser workflow',
      ],
    },
  ];
  return (
    <div className="min-h-screen bg-main-gradient bg-radial-overlay text-white flex flex-col">
      <Navbar />
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-8 md:py-0 mt-16">
        <section className="max-w-4xl w-full text-center mb-12 mt-12 md:mt-0 flex flex-col items-center justify-center">
          <h1 className="text-5xl md:text-6xl font-extrabold mb-4 text-primary-neon drop-shadow-neon">All Features</h1>
          <p className="text-lg md:text-2xl mb-8 text-text-light font-medium drop-shadow">Explore everything FuckHonorLock offers across desktop and browser.</p>
        </section>
        <section className="grid md:grid-cols-2 gap-10 w-full max-w-4xl mb-20">
          {features.map((feature, idx) => (
            <div key={feature.section} className="bg-primary-surface rounded-2xl p-8 shadow-neon border-2 border-primary-neon flex flex-col items-center hover:scale-105 hover:shadow-neon transition-transform duration-200 text-center">
              {feature.icon}
              <h2 className="text-2xl font-extrabold mb-2 text-primary-neon drop-shadow-neon">{feature.section}</h2>
              <ul className="text-primary-neon/90 text-left list-disc list-inside space-y-2 mt-4">
                {feature.items.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      </main>
      <footer className="bg-primary-dark text-primary-neon text-center py-4 text-xs opacity-90 border-t-2 border-primary-neon/30 shadow-inner mt-12">
        &copy; {new Date().getFullYear()} FuckHonorLock. Not affiliated with Honorlock. For educational use only.
      </footer>
    </div>
  );
}

function PurchasePage() {
  const { planId } = useParams();

  useEffect(() => {
    const startCheckout = async () => {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        alert('You must be logged in to purchase a plan.');
        return;
      }
      // Fetch profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('stripe_customer_id')
        .eq('id', user.id)
        .single();
      let customerId = profile?.stripe_customer_id;
      console.log('Fetched profile:', profile, 'error:', profileError);
      if (!customerId) {
        // Create Stripe customer
        const res = await fetch('/api/create-stripe-customer', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: user.email }),
        });
        const stripeData = await res.json();
        customerId = stripeData.customerId;
        console.log('Created new Stripe customer:', customerId);
        // Save to profile
        const { error: upsertError } = await supabase
          .from('profiles')
          .upsert({ id: user.id, stripe_customer_id: customerId });
        console.log('Upserted stripe_customer_id for user', user.id, 'error:', upsertError);
        // Fetch again to ensure it's saved and to avoid race conditions
        const { data: confirmProfile, error: confirmError } = await supabase
          .from('profiles')
          .select('stripe_customer_id')
          .eq('id', user.id)
          .single();
        customerId = confirmProfile?.stripe_customer_id || customerId;
        console.log('After upsert, profile:', confirmProfile, 'error:', confirmError);
      } else {
        console.log('Using existing Stripe customer:', customerId);
      }
      // Load Stripe.js
      const stripeJs = await loadStripeJs();
      // Call API to create checkout session with customerId
      const res = await fetch('/api/stripe-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planId, customerId }),
      });
      const session = await res.json();
      if (session && session.id) {
        stripeJs.redirectToCheckout({ sessionId: session.id });
      } else {
        alert(session.error || 'Failed to create session');
      }
    };
    startCheckout();
  }, [planId]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-primary-dark text-primary-neon">
      <h1 className="text-4xl font-extrabold mb-4">Redirecting to checkout...</h1>
      <p className="text-lg mb-6 text-primary">You will be redirected shortly.</p>
      <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-primary-neon border-b-4 border-primary-edge"></div>
    </div>
  );
}

function BillingPage() {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const location = useLocation();

  // Check for ?fromStripe=1
  const fromStripe = new URLSearchParams(location.search).get('fromStripe');

  React.useEffect(() => {
    if (fromStripe) {
      setLoading(false);
      return;
    }
    const goToPortal = async () => {
      setLoading(true);
      setError('');
      // 1. Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('You must be logged in.');
        setLoading(false);
        return;
      }
      // 2. Get profile (with stripe_customer_id)
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('stripe_customer_id')
        .eq('id', user.id)
        .single();
      if (profileError || !profile?.stripe_customer_id) {
        setError('No Stripe customer ID found for this user.');
        setLoading(false);
        return;
      }
      // 3. Call API
      const res = await fetch('/api/stripe-portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: profile.stripe_customer_id }),
      });
      const session = await res.json();
      if (session && session.url) {
        window.location.href = session.url;
      } else {
        setError(session.error || 'Failed to create portal session');
        setLoading(false);
      }
    };
    goToPortal();
  }, [fromStripe]);

  if (fromStripe) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-primary-dark text-primary-neon">
        <h1 className="text-4xl font-extrabold mb-4">Welcome Back!</h1>
        <p className="text-lg mb-6 text-primary">You have returned from managing your billing.</p>
        <button
          onClick={() => window.location.href = '/'}
          className="bg-primary-neon text-primary-dark font-bold px-6 py-2 rounded hover:bg-primary-edge hover:text-white transition ring-2 ring-primary-neon hover:ring-primary-edge"
        >
          Go Home
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-primary-dark text-primary-neon">
        <h1 className="text-4xl font-extrabold mb-4">Billing Error</h1>
        <p className="text-lg mb-6 text-primary">{error}</p>
        <button
          onClick={() => window.location.href = '/'}
          className="bg-primary-neon text-primary-dark font-bold px-6 py-2 rounded hover:bg-primary-edge hover:text-white transition ring-2 ring-primary-neon hover:ring-primary-edge"
        >
          Go Home
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-primary-dark text-primary-neon">
      <h1 className="text-4xl font-extrabold mb-4">Redirecting to billing portal...</h1>
      <p className="text-lg mb-6 text-primary">You will be redirected shortly.</p>
      <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-primary-neon border-b-4 border-primary-edge"></div>
    </div>
  );
}

// Helper to ensure a profile exists for every user
async function ensureProfileExists(user) {
  if (!user) return;
  const { data: profile } = await supabase
    .from('profiles')
    .select('id')
    .eq('id', user.id)
    .single();
  if (!profile) {
    await supabase.from('profiles').insert([
      {
        id: user.id,
        plan: 'free',
        credits: 30,
        last_refill: new Date().toISOString(),
        subscription_status: null,
        stripe_customer_id: null
      }
    ]);
  }
}

function AuthPage() {
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [mode, setMode] = React.useState('login'); // 'login' or 'signup'
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [user, setUser] = React.useState(null);
  const [profile, setProfile] = React.useState(null);
  const navigate = useNavigate();

  React.useEffect(() => {
    let ignore = false;
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!ignore) setUser(user);
      if (user) {
        supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .maybeSingle()
          .then(({ data }) => {
            if (data) setProfile(data);
          });
      }
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null);
      if (session?.user) {
        supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .maybeSingle()
          .then(({ data }) => {
            if (data) setProfile(data);
          });
      } else {
        setProfile(null);
      }
    });

    return () => {
      ignore = true;
      listener.subscription.unsubscribe();
    };
  }, []);

  // Polling for profile updates every 15 seconds, always starts when user is set
  React.useEffect(() => {
    let pollInterval = null;
    if (user) {
      pollInterval = setInterval(() => {
        supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .maybeSingle()
          .then(({ data }) => {
            if (data) setProfile(data);
          });
      }, 15000); // 15 seconds
    }
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      let user;
      if (mode === 'login') {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        const { data: userData } = await supabase.auth.getUser();
        user = userData.user;
        await ensureProfileExists(user);
      } else {
        const { data, error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
        user = data.user;
        if (user) {
          await ensureProfileExists(user);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  async function handleDownload() {
    const session = await supabase.auth.getSession();
    const token = session?.data?.session?.access_token;
    if (!token) {
      alert('You must be logged in!');
      return;
    }
    window.location.href = `/api/download-desktop?token=${token}`;
  }

  if (user) {
    // Calculate next refill date
    let lastRefill = profile?.last_refill ? parseISO(profile.last_refill) : new Date();
    let nextRefill = addDays(lastRefill, 30);
    let nextRefillStr = format(nextRefill, 'MMMM d, yyyy');
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-primary-dark text-primary-neon">
        <div className="w-full max-w-md flex flex-col gap-8">
          {/* Profile Info */}
          <div className="flex flex-col gap-1">
            <h2 className="text-3xl font-extrabold mb-1">Profile</h2>
            <span className="text-primary-neon/80 text-lg">{user.email}</span>
          {profile && (
              <div className="text-primary-neon/80 text-base mt-1">
              <div>Plan: <span className="font-bold text-primary-neon">{profile.plan || 'None'}</span></div>
              <div>Status: <span className="font-bold text-primary-neon">{profile.subscription_status || 'N/A'}</span></div>
            </div>
          )}
          </div>
          <hr className="border-primary-neon/30" />
          {/* Stats Section */}
          <div className="flex flex-col gap-1">
            <h3 className="text-xl font-bold mb-1 text-primary-neon">Stats</h3>
              <span className="text-lg">Credits: <span className="font-bold text-primary-neon">{(profile && typeof profile.credits === 'number') ? profile.credits : 0}</span></span>
              <span className="text-md text-primary-neon/80">Next refill: {nextRefillStr}</span>
            </div>
          <hr className="border-primary-neon/30" />
          {/* Download Desktop App Section */}
          <div className="flex flex-col gap-2">
            <h3 className="text-xl font-bold mb-1 text-primary-neon">Download Desktop App</h3>
            <button
              onClick={handleDownload}
              className="bg-primary-neon text-primary-dark font-bold px-8 py-3 rounded-full shadow-lg hover:bg-primary-edge hover:text-white transition text-lg w-fit"
            >
              Download for Windows
            </button>
          </div>
          <hr className="border-primary-neon/30" />
          {/* Actions */}
          <div className="flex gap-3 mt-2">
            <button onClick={() => navigate('/')} className="bg-primary-neon text-primary-dark font-bold px-6 py-2 rounded hover:bg-primary-edge hover:text-white transition ring-2 ring-primary-neon hover:ring-primary-edge">Home</button>
            <button onClick={handleLogout} className="bg-primary-edge text-white font-bold px-8 py-2 rounded hover:bg-primary-neon hover:text-primary-dark transition ring-2 ring-primary-edge hover:ring-primary-neon">Logout</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-primary-dark text-primary-neon">
      <div className="bg-primary rounded-2xl shadow-xl p-10 flex flex-col items-center w-full max-w-md">
        <h2 className="text-3xl font-extrabold mb-6 text-primary-neon drop-shadow-neon">{mode === 'login' ? 'Login' : 'Sign Up'}</h2>
        <form onSubmit={handleSubmit} className="w-full flex flex-col gap-4">
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            className="px-4 py-3 rounded bg-primary-dark text-primary-neon border border-primary-neon focus:outline-none focus:ring-2 focus:ring-primary-neon/60"
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            className="px-4 py-3 rounded bg-primary-dark text-primary-neon border border-primary-neon focus:outline-none focus:ring-2 focus:ring-primary-neon/60"
          />
          <button type="submit" disabled={loading} className="bg-primary-neon text-primary-dark font-bold px-8 py-3 rounded-full shadow-lg hover:bg-primary-edge hover:text-white transition text-lg">
            {loading ? 'Loading...' : mode === 'login' ? 'Login' : 'Sign Up'}
          </button>
        </form>
        <button onClick={() => setMode(mode === 'login' ? 'signup' : 'login')} className="mt-4 text-primary-edge hover:underline">
          {mode === 'login' ? 'Need an account? Sign Up' : 'Already have an account? Login'}
        </button>
        {error && <div className="mt-4 text-primary-edge font-semibold">{error}</div>}
      </div>
    </div>
  );
}

function SettingsPage() {
  return (
    <div>
      <h2>Settings</h2>
      <ul>
        <li><a href="/settings/billing">Manage Billing</a></li>
        <li><a href="/auth">Login/Signup</a></li>
      </ul>
    </div>
  );
}

// Helper to update the user's plan in Supabase
async function updateUserPlan(userId, newPlan) {
  const { error } = await supabase
    .from('profiles')
    .update({ plan: newPlan })
    .eq('id', userId);
  if (error) {
    console.error('Failed to update plan:', error.message);
  }
}

function SuccessPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // Optionally, you can extract the session_id from the query string if needed
  // const params = new URLSearchParams(location.search);
  // const sessionId = params.get('session_id');

  React.useEffect(() => {
    const timer = setTimeout(() => {
      navigate('/'); // Redirect to homepage or dashboard
    }, 3500);
    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-primary-dark text-primary-neon">
      <h1 className="text-4xl font-extrabold mb-4">Payment Successful!</h1>
      <p className="text-lg mb-6 text-primary">Thank you for your purchase. You will be redirected shortly.</p>
      <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-primary-neon border-b-4 border-primary-edge"></div>
    </div>
  );
}

function CancelPage() {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/'); // Redirect to homepage
  }, [navigate]);
  return null;
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<PlanSelection />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/purchase/:planId" element={<PurchasePage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/billing" element={<BillingPage />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/success" element={<SuccessPage />} />
        <Route path="/cancel" element={<CancelPage />} />
      </Routes>
    </Router>
  );
}

export default App;
